
import { getDb } from './firebase';
import type { Notification } from '../types';
// FIX: The Unsubscribe type is not available in the compat library directly. A function type is used instead.
// Also, DataSnapshot is imported for type safety in callbacks.
import type firebase from 'firebase/compat/app';

type Unsubscribe = () => void;


/**
 * Subscribes to a user's notifications in real-time.
 * @param userId The ID of the user (staff or admin).
 * @param callback The function to call with the updated list of notifications.
 * @returns An unsubscribe function to detach the listener.
 */
export const subscribeToNotifications = (userId: string, callback: (notifications: Notification[]) => void): Unsubscribe => {
    const db = getDb();
    if (!db) {
        console.warn("Firebase DB not initialized yet. Skipping notification subscription.");
        return () => {};
    }
    // FIX: Use compat `db.ref()`
    const notificationsRef = db.ref(`notifications/${userId}`);
    // FIX: Use compat `ref.on()` and manage the callback for unsubscribing.
    const onValueCallback = (snapshot: firebase.database.DataSnapshot) => {
        const data = snapshot.val();
        // Transforms the Firebase object into a sorted array of plain Notification objects.
        const notifications: Notification[] = data
            ? Object.keys(data).map(key => ({ id: key, ...data[key] })).sort((a, b) => new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime())
            : [];
        callback(notifications);
    };
    notificationsRef.on('value', onValueCallback);
    
    return () => notificationsRef.off('value', onValueCallback);
};

/**
 * Marks a single notification as read.
 * @param userId The ID of the user.
 * @param notificationId The ID of the notification to update.
 * @returns A promise that resolves when the update is complete.
 */
export const markNotificationAsRead = (userId: string, notificationId: string): Promise<void> => {
    const db = getDb();
    if (!db) return Promise.resolve();
    // FIX: Use compat `db.ref().set()`
    const notificationRef = db.ref(`notifications/${userId}/${notificationId}/read`);
    return notificationRef.set(true);
};

/**
 * Marks all unread notifications as read for a given user.
 * @param userId The ID of the user.
 * @param notifications The user's current list of notifications.
 * @returns A promise that resolves when all updates are complete.
 */
export const markAllNotificationsAsRead = async (userId: string, notifications: Notification[]): Promise<void> => {
    const unreadNotifications = notifications.filter(n => !n.read);
    if (unreadNotifications.length > 0) {
        const promises = unreadNotifications.map(n => markNotificationAsRead(userId, n.id));
        await Promise.all(promises);
    }
};

/**
 * Deletes a single notification.
 * @param userId The ID of the user.
 * @param notificationId The ID of the notification to delete.
 * @returns A promise that resolves when the deletion is complete.
 */
export const deleteNotification = (userId: string, notificationId: string): Promise<void> => {
    const db = getDb();
    if (!db) return Promise.resolve();
    const notificationRef = db.ref(`notifications/${userId}/${notificationId}`);
    return notificationRef.remove();
};

/**
 * Clears all notifications for a specific user.
 * @param userId The ID of the user.
 * @returns A promise that resolves when all notifications are cleared.
 */
export const clearNotifications = (userId: string): Promise<void> => {
    const db = getDb();
    if (!db) return Promise.resolve();
    const notificationsRef = db.ref(`notifications/${userId}`);
    return notificationsRef.remove();
};

/**
 * Creates a new notification for a specific user. This function simulates a
 * backend or trusted environment action.
 * @param userId The ID of the user to notify.
 * @param notificationData The content of the notification.
 * @returns A promise that resolves when the notification is created.
 */
export const createNotification = (userId: string, notificationData: Omit<Notification, 'id' | 'read' | 'scheduledAt'>): Promise<void> => {
    const db = getDb();
    if (!db) {
        console.warn("Firebase DB not initialized yet. Cannot create notification.");
        return Promise.resolve();
    }
    // FIX: Use compat `db.ref()`
    const notificationsRef = db.ref(`notifications/${userId}`);
    // FIX: Use compat `ref.push()`
    const newNotificationRef = notificationsRef.push();
    const newNotification: Omit<Notification, 'id'> = {
        ...notificationData,
        read: false,
        scheduledAt: new Date().toISOString(), // Ensures date is a valid ISO string.
    };
    // FIX: Use compat `ref.set()` on the new ref.
    return newNotificationRef.set(newNotification);
};